// © 2026 AttainIP Engineering
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchEnterpriseGuests,
  fetchEnterprises,
  type EnterpriseSummary,
  type GuestMembership,
} from '@/api/queries';
import { api } from '@/api/client';
import { Card } from '@/components/Card';
import { defaultHomeOrgId, homeOrgOptions, orgLabel } from '@/lib/guestOrgs';

/**
 * Guest access to one firm, granted by a PLATFORM admin (RFC-0510 W44).
 *
 * The console had no grant at all. W8 shipped exactly one surface over the
 * membership record — Team → Guest access in the enterprise app — and only the
 * HOST firm's own org_admin can reach it. So an Attain employee could not be
 * made a guest of a client until somebody at that client had signed in and been
 * made an admin, which is the wrong way round: the employee is needed on day
 * one, during onboarding, before the firm has any users of its own.
 *
 * ⚠️ THE HOME ORGANISATION IS CHOSEN, NEVER TYPED (RFC-0510 W30). It is a
 * UUID on the wire and a UUID is not something an operator can be asked for —
 * W38 had to rebuild the Case Ownership page for exactly that reason, after it
 * shipped asking for two engine-internal numbers that appear nowhere in the
 * product. The options here are the firms the console already lists, labelled
 * by name and domain, and the raw UUID is never rendered.
 *
 * ⚠️ REVOKING IS GATED ON OWNERSHIP, AND THE GATE LIVES IN THE BFF. This
 * component does not decide anything about it — it renders the refusal, names
 * the cases, and points at the page that can move them. A guest whose cases
 * were orphaned by a revocation has no path back (RFC-0168 §15.4).
 */

interface OwnedCaseRef {
  case_id: number | string | null;
  name: string | null;
}

interface RevokeRefusal {
  message: string;
  cases: OwnedCaseRef[];
  email: string;
}

function timestampLabel(iso: string | null): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleDateString();
}

function errorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  if (typeof detail === 'string') return detail;
  const message = (detail as { message?: string } | undefined)?.message;
  return message || fallback;
}

function GrantGuestDialog({
  orgId,
  options,
  onClose,
}: {
  orgId: string;
  options: EnterpriseSummary[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [homeOrgId, setHomeOrgId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The default is applied once the options arrive, and only while the operator
  // has not chosen for themselves.
  useEffect(() => {
    setHomeOrgId((current) => current || defaultHomeOrgId(options));
  }, [options]);

  const handleGrant = async () => {
    const address = email.trim();
    if (!address.includes('@')) {
      setError('Enter the email address this person signs in with.');
      return;
    }
    if (!homeOrgId) {
      setError('Choose the organisation this person belongs to.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // `kind` and `granted_by_user_id` are deliberately NOT sent: the BFF
      // fixes the first and takes the second from the JWT. Sending them would
      // be ignored, and sending them anyway would imply they were ours to set.
      await api.post(`/v1/enterprises/${orgId}/guests`, {
        email: address,
        home_organization_id: homeOrgId,
      });
      queryClient.invalidateQueries({ queryKey: ['enterprise-guests', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprise-users', orgId] });
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to grant guest access'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Grant guest access"
        className="w-full max-w-md rounded bg-white p-4 shadow-lg"
      >
        <h2 className="text-sm font-semibold mb-3">Grant guest access</h2>
        <div className="space-y-3">
          <div>
            <label htmlFor="guest-email" className="block text-xs text-gray-500 mb-1">
              Email
            </label>
            <input
              id="guest-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@attainip.ai"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="guest-home-org" className="block text-xs text-gray-500 mb-1">
              Home organisation
            </label>
            {/* A SELECT, never a text field. See the module note on W30. */}
            <select
              id="guest-home-org"
              value={homeOrgId}
              onChange={(e) => setHomeOrgId(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select an organisation…</option>
              {options.map((org) => (
                <option key={org.org_id!} value={org.org_id!}>
                  {orgLabel(org)}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">
              The organisation this person belongs to. A guest belongs elsewhere — this
              firm is not on the list.
            </p>
          </div>
          {options.length === 0 && (
            <p className="text-xs text-red-600">
              No other activated organisation exists to be a home organisation yet.
            </p>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={handleGrant}
              disabled={saving}
              className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? 'Granting...' : 'Grant access'}
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GuestsPanel({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [granting, setGranting] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<RevokeRefusal | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['enterprise-guests', orgId, includeRevoked],
    queryFn: () => fetchEnterpriseGuests(orgId, includeRevoked),
  });

  // The firms list this console already loads — the source of both the home-org
  // options and the NAME a guest's home organisation is shown under. One read
  // for the whole panel; never one per row.
  const { data: enterprises } = useQuery({
    queryKey: ['enterprises'],
    queryFn: fetchEnterprises,
  });

  // Memoised because two useMemos below depend on it; `?? []` would otherwise
  // be a fresh array on every render and recompute both every time.
  const allOrgs: EnterpriseSummary[] = useMemo(
    () => enterprises?.data ?? [],
    [enterprises],
  );
  const options = useMemo(() => homeOrgOptions(allOrgs, orgId), [allOrgs, orgId]);
  const orgById = useMemo(() => {
    const byId = new Map<string, EnterpriseSummary>();
    allOrgs.forEach((org) => {
      if (org.org_id) byId.set(org.org_id, org);
    });
    return byId;
  }, [allOrgs]);

  const guests: GuestMembership[] = data?.data ?? [];

  const handleRevoke = async (guest: GuestMembership) => {
    if (
      !window.confirm(
        `Revoke ${guest.email}'s guest access to this firm? The membership row is ` +
          'kept as history.',
      )
    ) {
      return;
    }
    setRevoking(guest.email);
    setRefusal(null);
    setRevokeError(null);
    try {
      await api.delete(`/v1/enterprises/${orgId}/guests`, {
        params: { email: guest.email },
      });
      queryClient.invalidateQueries({ queryKey: ['enterprise-guests', orgId] });
      queryClient.invalidateQueries({ queryKey: ['enterprise-users', orgId] });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response
        ?.data?.detail as
        | { code?: string; message?: string; cases?: OwnedCaseRef[] }
        | undefined;
      if (detail?.code === 'guest_owns_cases') {
        // NOT an alert. The operator's next action is to go and reassign
        // specific cases, so the cases stay on screen while they do it.
        setRefusal({
          message: detail.message ?? 'This guest still owns cases in this firm.',
          cases: detail.cases ?? [],
          email: guest.email,
        });
      } else {
        setRevokeError(errorMessage(err, 'Failed to revoke guest access'));
      }
    } finally {
      setRevoking(null);
    }
  };

  return (
    <Card title={`Guests (${guests.length})`}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <p className="text-xs text-gray-400">
          People from another organisation who may act inside this firm. Granting here
          needs no user of this firm to exist yet.
        </p>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <input
              type="checkbox"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
              className="rounded"
            />
            Show revoked
          </label>
          <button
            onClick={() => setGranting(true)}
            className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded hover:bg-gray-800"
          >
            Grant guest
          </button>
        </div>
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading guests...</p>}
      {error && <p className="text-sm text-red-600">Failed to load guests</p>}

      {refusal && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3">
          <p className="text-xs text-amber-800">{refusal.message}</p>
          <ul className="mt-2 space-y-1">
            {refusal.cases.map((c, i) => (
              <li key={`${c.case_id ?? 'case'}-${i}`} className="text-xs font-mono">
                #{String(c.case_id ?? '—')}
                {c.name ? ` — ${c.name}` : ''}
              </li>
            ))}
          </ul>
          <Link
            to="/case-ownership"
            className="mt-2 inline-block text-xs text-blue-600 hover:underline"
          >
            Reassign these on the Case ownership page →
          </Link>
        </div>
      )}
      {revokeError && <p className="mb-3 text-sm text-red-600">{revokeError}</p>}

      {!isLoading && !error && guests.length === 0 && (
        <p className="text-sm text-gray-400">No guests of this firm</p>
      )}

      {!isLoading && !error && guests.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium">Email</th>
              <th className="pb-2 font-medium">Home organisation</th>
              <th className="pb-2 font-medium">Granted by</th>
              <th className="pb-2 font-medium">Granted</th>
              <th className="pb-2 font-medium">Status</th>
              <th className="pb-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {guests.map((guest) => {
              const home = guest.home_organization_id
                ? orgById.get(guest.home_organization_id)
                : undefined;
              const revoked = guest.status === 'revoked';
              return (
                <tr key={guest.id} className="border-b last:border-0">
                  <td className="py-2 font-mono text-xs">{guest.email}</td>
                  <td className="py-2">
                    {home ? (
                      <>
                        {home.name}
                        <span className="block text-xs text-gray-400">{home.domain}</span>
                      </>
                    ) : (
                      // The console shows what it knows. A home org the
                      // enterprises list does not carry is named as unknown
                      // rather than rendered as a UUID nobody can read.
                      <span className="text-xs text-gray-400">Unknown organisation</span>
                    )}
                  </td>
                  <td className="py-2">
                    <Link
                      to={`/users/${guest.granted_by_user_id}`}
                      className="text-xs text-blue-600 hover:underline font-mono"
                    >
                      {guest.granted_by_user_id.slice(0, 8)}
                    </Link>
                  </td>
                  <td className="py-2 text-xs">{timestampLabel(guest.created_at)}</td>
                  <td className="py-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        revoked ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {guest.status}
                    </span>
                    {revoked && guest.revoked_at && (
                      <span className="block text-xs text-gray-400">
                        {timestampLabel(guest.revoked_at)}
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {!revoked && (
                      <button
                        onClick={() => handleRevoke(guest)}
                        disabled={revoking === guest.email}
                        className="text-xs px-2 py-0.5 rounded border bg-red-50 text-red-600 border-red-200 hover:bg-red-100 disabled:opacity-50"
                      >
                        {revoking === guest.email ? '...' : 'Revoke'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {granting && (
        <GrantGuestDialog
          orgId={orgId}
          options={options}
          onClose={() => setGranting(false)}
        />
      )}
    </Card>
  );
}
